import * as React from 'react';
import { MutableRefObject, ReactElement, useCallback, useState } from 'react';
import { SearchDateTerms } from '../../types/common/searchfilter';
import { CLOSETGroupsManager } from '../../types/api/groups/closet';
import {
  initialGroupsSearchFilter,
  paymentMethodOptions,
  planTypeOptions,
} from '../../constants/groups/filter';
import { useCLOSETGroupsManagersQuery } from 'hooks/groups/useCLOSETGroupsManagersQuery';
import { GroupsRef } from 'components/groups/GroupList';
import { CLOSETGroupsInitialSearchParams } from '../../hooks/groups/useCLOSETGroupsInitialSearchParams';
import FilterLayout from '../common/searchfilter/FilterLayout';
import DateRangeFilter from '../common/searchfilter/contents/DateRangeFilter';
import CheckboxFilterElement from '../common/searchfilter/contents/CheckboxFilterElement';
import KeywordInput from '../common/searchfilter/contents/KeywordInput';
import FilterElementLayout from '../common/searchfilter/FilterElementLayout';
import AutocompleteInput from '../common/searchfilter/AutocompleteInput';
import keyboardKey from 'keyboard-key';

interface Props {
  groupsRef?: MutableRefObject<GroupsRef | null>;
  initialSearchParams: CLOSETGroupsInitialSearchParams;
}

const SearchFilter = ({ groupsRef, initialSearchParams }: Props): ReactElement => {
  const [dateInterval, setDateInterval] = useState<string[]>(initialSearchParams.dateInterval);
  const [dateTerm, setDateTerm] = useState<SearchDateTerms | null>(initialSearchParams.dateTerm);
  const [planTypeValues, setPlanTypeValues] = useState<number[]>(
    initialSearchParams.planTypeValues,
  );
  const [paymentMethodValues, setPaymentMethodValues] = useState<number[]>(
    initialSearchParams.paymentMethodValues,
  );
  const [keyword, setKeyword] = useState<string>(initialSearchParams.keyword);

  const [manager, setManager] = useState<CLOSETGroupsManager | null>(
    initialGroupsSearchFilter.manager,
  );

  const managerOptions = useCLOSETGroupsManagersQuery({
    managerEmail: initialSearchParams.managerEmail,
    setManager,
  });

  const handleSearch = useCallback(
    (states: {
      dateInterval: string[];
      dateTerm: SearchDateTerms | null;
      planTypeValues: number[];
      paymentMethodValues: number[];
      keyword: string;
      manager: CLOSETGroupsManager | null;
    }): Promise<void> | void => {
      if (groupsRef?.current?.fetchGroupList === null) return;
      const { dateInterval, planTypeValues, paymentMethodValues, keyword, manager } = states;
      const [startDate, endDate] = dateInterval;
      return groupsRef?.current?.fetchGroupList(
        {
          startDate,
          endDate,
          planTypes: planTypeValues,
          paymentMethods: paymentMethodValues,
          keyword,
          managerEmail: manager?.email || '',
        },
        1,
      );
    },
    [groupsRef],
  );

  const setSearchParams = useCallback((): void => {
    const url = new URL(window.location.href);
    url.searchParams.set('dateInterval', dateInterval.join(','));
    url.searchParams.set('planTypeValues', planTypeValues.join(','));
    url.searchParams.set('paymentMethodValues', paymentMethodValues.join(','));
    url.searchParams.set('keyword', keyword);
    if (dateTerm) {
      url.searchParams.set('dateTerm', dateTerm.toString());
    }
    if (manager) {
      url.searchParams.set('manager', manager.email);
    }
    window.history.replaceState(null, '', url.toString());
  }, [dateInterval, dateTerm, planTypeValues, paymentMethodValues, keyword, manager]);

  const deleteSearchParams = (): void => {
    const url = new URL(window.location.href);
    url.searchParams.delete('dateInterval');
    url.searchParams.delete('planTypeValues');
    url.searchParams.delete('paymentMethodValues');
    url.searchParams.delete('keyword');
    url.searchParams.delete('dateTerm');
    url.searchParams.delete('manager');
    window.history.replaceState(null, '', url.toString());
  };

  const onClickSearch = useCallback((): Promise<void> | void => {
    setSearchParams();
    return handleSearch({
      dateInterval,
      dateTerm,
      planTypeValues,
      paymentMethodValues,
      keyword,
      manager,
    });
  }, [dateInterval, dateTerm, planTypeValues, paymentMethodValues, keyword, manager, handleSearch]);

  const onClickReset = async (): Promise<void> => {
    setDateInterval(initialGroupsSearchFilter.dateInterval);
    setDateTerm(initialGroupsSearchFilter.dateTerm);
    setKeyword(initialGroupsSearchFilter.keyword);
    setPlanTypeValues(initialGroupsSearchFilter.planTypeValues);
    setPaymentMethodValues(initialGroupsSearchFilter.paymentMethodValues);
    setManager(initialGroupsSearchFilter.manager);
    await handleSearch(initialGroupsSearchFilter);
    return deleteSearchParams();
  };

  const onKeyDownKeyword = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const inputKey = keyboardKey.getCode(e);
    if (inputKey !== keyboardKey.Enter) {
      return;
    }
    return onClickSearch();
  };

  const onResetManager = () => {
    setManager(null);
    const url = new URL(window.location.href);
    url.searchParams.delete('manager');
    return window.history.replaceState(null, '', url.toString());
  };

  return (
    <FilterLayout getSearchResult={onClickSearch} resetFilter={onClickReset}>
      <DateRangeFilter
        label={'Subscription Term Date'}
        dateInterval={dateInterval}
        setDateInterval={setDateInterval}
        selectedShortcut={dateTerm}
        setSelectedShortcut={setDateTerm}
      />
      <CheckboxFilterElement
        label={'Plan Type'}
        values={planTypeValues}
        options={planTypeOptions}
        onChange={setPlanTypeValues}
      />
      <CheckboxFilterElement
        label={'Payment Method'}
        values={paymentMethodValues}
        options={paymentMethodOptions}
        onChange={setPaymentMethodValues}
      />
      <FilterElementLayout label={'CLO-SET Manager'} gridSize={1}>
        <AutocompleteInput<CLOSETGroupsManager>
          value={manager}
          onChange={setManager}
          options={managerOptions.data}
          getOptionLabel={managerOptions.getManagerLabel}
          onReset={onResetManager}
        />
      </FilterElementLayout>
      <FilterElementLayout label={'Keyword'} gridSize={1}>
        <KeywordInput
          keyword={keyword}
          onChangeKeyword={e => setKeyword(e.target.value)}
          onKeyDownKeyword={onKeyDownKeyword}
          placeholder={'Group ID, Group admin Email, Domain'}
          maxLength={200}
        />
      </FilterElementLayout>
    </FilterLayout>
  );
};

export default SearchFilter;
